import { Client } from "@core/Client";
import { Constants } from "@core/Utils/Constants";
import { setTextFontScaledToBox } from "@core/Utils/HelperFunction";
import { choice_question, questionStakeData } from "../../Logic/Links/IMAXA/IMAXAProtocol";
import { Language, Messages } from "./../Language";

export class QuestionDialog extends Phaser.GameObjects.Container {
    protected callback: Function = null;
    protected background: Phaser.GameObjects.Graphics;

    constructor(public scene: Phaser.Scene, callback: Function, width: number, height: number, portraitMode: Boolean) {
        super(scene);
        this.callback = callback;

        this.background = new Phaser.GameObjects.Graphics(scene);
        this.background.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
        this.background.on('pointerup', (p, x, y, e) => {
            e.stopPropagation();
        }, this);
        this.background.on('pointerdown', (p, x, y, e) => {
            e.stopPropagation();
        }, this);
        this.add(this.background);
        this.OnResize(width, height, portraitMode);
    }

    public ResponseSelected(selectedResponse: any): void {
        if (this.callback)
            this.callback(selectedResponse);
    }

    public OnResize(width: number, height: number, portraitMode: Boolean = false): void {
        this.background.clear();
        this.background.fillStyle(0x000000, Constants.PANEL.LOCK_SCREEN_OPACITY);
        if (portraitMode) {
            this.background.fillRect(0, -height, width, height);
            this.background.input.hitArea.setPosition(0, -height);
        } else {
            this.background.fillRect(0, 0, width, height);
            this.background.input.hitArea.setPosition(0, 0);
        }
        this.background.input.hitArea.setSize(width, height);
    }
}

export class ChoiceQuestion extends QuestionDialog {
    private maxNumChoices: number
    private minNumChoices: number
    private question: string
    private topic: string
    private ID: number

    private currentWidth: number = 0;
    private buttonPadding: number = 0;
    private buttonHeight: number = 0;
    private buttonWidth: number = 0;
    private numberOfButtons: number = 0;

    private panelSlider: Phaser.GameObjects.Image;//only shows up if there are more than 3 choices to pick from

    private questionTitle: Phaser.GameObjects.Text;
    private buttons: Array<SelectButton> = new Array<SelectButton>();

    constructor(public scene: Phaser.Scene, callback: Function, width: number, height: number, portraitMode: Boolean, private choiceObject: choice_question, private requiresConfirmation: boolean = false) {
        super(scene, callback, width, height, portraitMode);

        this.currentWidth = width;

        this.question = choiceObject.question;
        this.topic = choiceObject.topic;
        this.ID = choiceObject.ID;

        this.maxNumChoices = choiceObject.maxNumChoices;
        this.minNumChoices = choiceObject.minNumChoices;

        this.numberOfButtons = choiceObject.choices.length;
        this.buttonHeight = Math.min(height / 2.5, width / 4.5);
        this.buttonWidth = this.buttonHeight * 1.25;
        this.buttonPadding = this.buttonHeight / 10;

        //assign original choice IDs so that displaying them does not break sent ID numbers
        choiceObject.choices.forEach((choice, i) => {
            choice.originalIndex = i;
        });

        if (this.topic == "stake") {
            if (choiceObject.choices.length > 1) {
                function compare(a, b) {
                    if (a.data.betTypeMax[0] == 0)
                        return 1;
                    else if (b.data.betTypeMax[0] == 0)
                        return -1;
                    else
                        return a.data.betTypeMax[0] - b.data.betTypeMax[0];
                }
                choiceObject.choices.sort(compare);
            }

            choiceObject.choices.forEach((choice, i) => {
                let buttonXPositions;
                if (i > 2)
                    buttonXPositions = this.buttons[i - 1].x + (this.buttonPadding + this.buttonWidth);
                else
                    buttonXPositions = width / 2 - ((this.buttonPadding + this.buttonWidth) * (Math.min(3, this.numberOfButtons) - (i + 1))) + ((this.buttonPadding * 1.5 + this.buttonWidth) * Math.min(2, this.numberOfButtons - 1) * 0.5) - (this.buttonPadding + this.buttonWidth) / 2;

                let button = new StakeButton(scene, () => { this.ButtonClickCallback() }, choice.originalIndex, buttonXPositions, height / 2 - this.buttonHeight / 2, this.buttonWidth, this.buttonHeight,
                    Language.Get(Messages.QUESTION_STAKE) + " " + (i + 1).toString(), choice, choice.data as questionStakeData, i == (choiceObject.choices.length - 1));
                this.buttons.push(button);
                this.add(button);
            }, this);

            this.questionTitle = new Phaser.GameObjects.Text(scene, width / 2, height / 2 - (this.buttonHeight / 2 + this.buttonPadding * 2), Language.Get(Messages.SELECT_STAKE_TO_PLAY), {
                fontFamily: "Arial",
                align: "center",
                fontSize: "7vw",
                fontStyle: "normal",
            });
            this.questionTitle.setOrigin(0.5, 1);
            setTextFontScaledToBox(this.questionTitle, width / 2, (height - this.buttonHeight) / 2);

            this.add(this.questionTitle);
        }

        if (this.numberOfButtons > 3) {
            this.panelSlider = new Phaser.GameObjects.Image(scene, width / 2, height / 2 + (this.buttonHeight / 2 + this.buttonPadding * 3), Constants.SPRITESHEET_MENU, "Menu_SliderImage.png");
            this.panelSlider.setOrigin(0.5);
            this.panelSlider.setDisplaySize(this.buttonHeight / 7, this.buttonHeight / 7);
            this.panelSlider.slider = this.scene.plugins.get('sliderplugin').add(this.panelSlider, {
                endPoints: [{
                    x: this.panelSlider.x - width / 4,
                    y: this.panelSlider.y
                },
                {
                    x: this.panelSlider.x + width / 4,
                    y: this.panelSlider.y
                }
                ],
                value: 0
            });

            this.panelSlider.slider.on('valuechange', function (newValue, prevValue) {
                if (this.buttons) {
                    this.buttons.forEach((button, i) => {
                        let buttonXPositions;
                        if (i > 2)
                            buttonXPositions = this.buttons[i - 1].x + (this.buttonPadding + this.buttonWidth);
                        else
                            buttonXPositions = this.currentWidth / 2 - ((this.buttonPadding + this.buttonWidth) * (Math.min(3, this.numberOfButtons) - (i + 1))) + ((this.buttonPadding * 1.5 + this.buttonWidth) * Math.min(2, this.numberOfButtons - 1) * 0.5) - (this.buttonPadding + this.buttonWidth) / 2 - (((this.buttonWidth + this.buttonPadding) * (this.numberOfButtons - 3)) * newValue);
                        button.x = buttonXPositions;
                    });
                }
            }.bind(this));
            this.add(this.panelSlider);

            this.background.lineStyle(3, 0xffffff, 1).strokePoints(this.panelSlider.slider.endPoints);
        }
    }

    private ButtonClickCallback(): void {
        console.log("Clicked on question button");
        let selected: Array<number> = new Array<number>();
        this.buttons.forEach((button, i) => {
            if (button.selected)
                selected.push(button.originalIndex);
        });

        if (!this.requiresConfirmation && selected.length >= this.minNumChoices && selected.length <= this.maxNumChoices)
            this.callback({ question: this.ID, answer: selected });
    }

    public override OnResize(width: number, height: number, portraitMode: boolean = false): void {
        super.OnResize(width, height, portraitMode);

        this.currentWidth = width;
        this.buttonHeight = Math.min(height / 2.5, width / 4.5);
        this.buttonWidth = this.buttonHeight * 1.25;
        this.buttonPadding = this.buttonHeight / 10;

        if (this.questionTitle) {
            this.questionTitle.setPosition(width / 2, height / 2 - (this.buttonHeight / 2 + this.buttonPadding * 2));
            setTextFontScaledToBox(this.questionTitle, width / 2, (height - this.buttonHeight) / 2);
            if (portraitMode)
                this.questionTitle.y -= height;
        }

        if (this.buttons) {
            this.buttons.forEach((button, i) => {
                let buttonXPositions;
                if (i > 2)
                    buttonXPositions = this.buttons[i - 1].x + (this.buttonPadding + this.buttonWidth);
                else
                    buttonXPositions = width / 2 - ((this.buttonPadding + this.buttonWidth) * (Math.min(3, this.numberOfButtons) - (i + 1))) + ((this.buttonPadding * 1.5 + this.buttonWidth) * Math.min(2, this.numberOfButtons - 1) * 0.5) - (this.buttonPadding + this.buttonWidth) / 2;
                button.OnResize(this.buttonWidth, this.buttonHeight, portraitMode);
                button.x = buttonXPositions;
                button.y = height / 2 - this.buttonHeight / 2;
                if (portraitMode)
                    button.y -= height;
            });
        }

        if (this.numberOfButtons > 3 && this.panelSlider) {
            let sliderStartPos = width * 0.25;
            let sliderEndPos = width * 0.75;

            this.panelSlider.setDisplaySize(this.buttonHeight / 7, this.buttonHeight / 7);
            this.panelSlider.setPosition(sliderStartPos + (sliderEndPos - sliderStartPos) * this.panelSlider.slider.value, height / 2 + (this.buttonHeight / 2 + this.buttonPadding * 3));
            if (portraitMode)
                this.panelSlider.y -= height;

            this.panelSlider.slider.endPoints = [{
                x: sliderStartPos,
                y: this.panelSlider.y
            },
            {
                x: sliderEndPos,
                y: this.panelSlider.y
            }];

            this.background.lineStyle(3, 0xffffff, 1).strokePoints(this.panelSlider.slider.endPoints);

            this.buttons.forEach(button => {
                button.x -= ((this.buttonWidth + this.buttonPadding) * (this.numberOfButtons - 3)) * this.panelSlider.slider.value;
            });
        }
    }
}

export class SelectButton extends Phaser.GameObjects.Container {
    public selected: boolean = false;

    protected portraitMode: boolean = false;

    private background: RexPlugins.GameObjects.NinePatch;

    constructor(scene: Phaser.Scene, protected callback: Function, public originalIndex: number, x: number, y: number, width: number, height: number) {
        super(scene);

        this.setPosition(x, y);

        this.background = new RexPlugins.GameObjects.NinePatch(scene, 0, 0, width, height, Constants.SPRITESHEET_MENU, "MenuBackground.png", [16, undefined, 16], [16, undefined, 16], { edge: 'scale', internal: 'scale' });
        this.background.setOrigin(0);
        this.background.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
        this.background.on('pointerup', (p, x, y, e) => {
            this.selected = !this.selected;
            this.OnResize(this.background.width, this.background.height, this.portraitMode);
            this.callback();
        }, this);
        this.add(this.background);
    }

    OnResize(width: number, height: number, portraitMode: boolean): void {
        this.portraitMode = portraitMode;
        /*
        if (this.selected)
            this.background.tint = 0x555555;
        else
            this.background.clearTint();
        */
        this.background.setSize(width, height);
        this.background.input.hitArea.setSize(width, height);
    }
}

export class StakeButton extends SelectButton {

    //private debugIndexText: Phaser.GameObjects.Text;

    private titleText: Phaser.GameObjects.Text;
    private descriptionText: Phaser.GameObjects.Text;
    private stakeInfoText: Phaser.GameObjects.Text;
    private stakeString: string;

    private highStakeIcon: Phaser.GameObjects.Image;
    private highStakeShadow: Phaser.GameObjects.Image;

    constructor(scene: Phaser.Scene, protected callback: Function, originalIndex: number, x: number, y: number, width: number, height: number, private title, private choice, private questionData: questionStakeData, private highStake: boolean = false) {
        super(scene, callback, originalIndex, x, y, width, height);
        let titleTextToDisplay: string = "";
        if (highStake) {
            titleTextToDisplay = "~" + title + "~";
        }
        else {
            titleTextToDisplay = " " + title + " ";
        }
        this.titleText = new Phaser.GameObjects.Text(scene, width / 2, 0, titleTextToDisplay, {
            fontFamily: "Arial",
            align: "center",
            fontStyle: "bold",
            color: '#ffffff',
        });
        this.titleText.setOrigin(0.5, 0);
        this.titleText.setShadow(3, 3, 'rgba(0,0,0,1)', 5);

        if (choice.description.length < 1)
            choice.description = Language.Get(Messages.QUESTION_STAKE_DESCRIPTION_SUBTEXT);

        this.descriptionText = new Phaser.GameObjects.Text(scene, width / 2, height / 4, choice.description, {
            fontFamily: "Arial",
            align: "center",
            fontStyle: "normal",
            color: '#099000',
            wordWrap: {
                width: width,
                useAdvancedWrap: true
            }
        });
        this.descriptionText.setOrigin(0.5, 0);
        this.descriptionText.setShadow(3, 3, 'rgba(0,0,0,1)', 5);

        this.stakeInfoText = new Phaser.GameObjects.Text(scene, width / 2, height / 2, "", {
            fontFamily: "Arial",
            align: "center",
            fontStyle: "normal",
            color: '#ffffff',
            wordWrap: {
                width: width,
                useAdvancedWrap: true
            }
        });
        this.stakeInfoText.setOrigin(0.5, 1);
        this.stakeInfoText.setShadow(3, 3, 'rgba(0,0,0,1)', 5);

        this.stakeInfoText.text = Language.Get(Messages.MIN_TABLE_BET) + " : ";
        if (this.questionData.betTypeMax[0] == 0)
            this.stakeInfoText.text += "/";
        else
            this.stakeInfoText.text += Language.InternationalNumberFormat.format(Client.logic.BalanceManager.CreditToMoney(this.questionData.betTypeMin[0]));
        this.stakeInfoText.text += "\n" + Language.Get(Messages.MAX_TABLE_BET) + " : "
        if (this.questionData.betTypeMax[0] == 0)
            this.stakeInfoText.text += "/";
        else
            this.stakeInfoText.text += Language.InternationalNumberFormat.format(Client.logic.BalanceManager.CreditToMoney(this.questionData.betTypeMax[0]));

        let textPadding = (width / 100) * 5;

        this.setBiggestPossibleFont(this.titleText, width - 2 * textPadding, (height / 4) - 2 * textPadding);
        this.setBiggestPossibleFont(this.descriptionText, width - 2 * textPadding, (height / 4) - 2 * textPadding);
        this.setBiggestPossibleFont(this.stakeInfoText, width - 2 * textPadding, (height / 4) - 2 * textPadding);

        this.titleText.setPosition(width / 2, textPadding * 2);
        this.descriptionText.setPosition(width / 2, height / 4 + textPadding);
        this.stakeInfoText.setPosition(width / 2, height - textPadding);

        this.add(this.titleText);
        this.add(this.descriptionText);
        this.add(this.stakeInfoText);

        if (highStake) {
            this.highStakeIcon = new Phaser.GameObjects.Image(scene, this.titleText.x, this.titleText.y, Constants.SPRITESHEET_WINS, "winIcon.png");
            this.highStakeIcon.setDisplaySize(textPadding * 2, textPadding * 2);
            this.highStakeIcon.setScale(Math.min(this.highStakeIcon.scaleX, this.highStakeIcon.scaleY));
            this.highStakeIcon.setOrigin(0.5, 1);

            this.highStakeShadow = new Phaser.GameObjects.Image(scene, this.highStakeIcon.x + 3, this.highStakeIcon.y + 3, Constants.SPRITESHEET_WINS, "winIcon.png");
            this.highStakeShadow.setDisplaySize(textPadding * 2, textPadding * 2);
            this.highStakeShadow.setScale(Math.min(this.highStakeShadow.scaleX, this.highStakeShadow.scaleY));
            this.highStakeShadow.setOrigin(0.5, 1);
            this.highStakeShadow.setTint(0x000000);
            this.highStakeShadow.setAlpha(0.9);
            this.add(this.highStakeShadow);
            this.add(this.highStakeIcon);

            this.titleText.setColor('#ffd500');
            this.stakeInfoText.setColor('#ffd500');
        }
        /*
        this.debugIndexText = new Phaser.GameObjects.Text(scene, 0, 0, this.originalIndex.toString(), {
            fontFamily: "Arial",
            align: "center",
            fontStyle: "normal",
            color: '#777777'
        });
        this.debugIndexText.setFontSize(20);
        this.add(this.debugIndexText);
        */
    }

    override OnResize(width: number, height: number, portraitMode: boolean = false): void {
        super.OnResize(width, height, portraitMode);

        let textPadding = (width / 100) * 5;

        /*
        if (this.selected)
            this.titleText.tint = 0x550000;
        else
            this.titleText.clearTint();
        */

        this.setBiggestPossibleFont(this.titleText, width - 2 * textPadding, (height / 4) - 2 * textPadding);
        this.setBiggestPossibleFont(this.descriptionText, width - 2 * textPadding, (height / 4) - 2 * textPadding);
        this.setBiggestPossibleFont(this.stakeInfoText, width - 2 * textPadding, (height / 4) - 2 * textPadding);

        this.titleText.setPosition(width / 2, textPadding * 2);
        this.descriptionText.setPosition(width / 2, height / 4 + textPadding);
        this.stakeInfoText.setPosition(width / 2, height - textPadding);

        if (this.highStake && this.highStakeShadow) {
            this.highStakeIcon.setDisplaySize(textPadding * 2, textPadding * 2);
            this.highStakeIcon.setPosition(this.titleText.x, this.titleText.y);

            this.highStakeShadow.setDisplaySize(textPadding * 2, textPadding * 2);
            this.highStakeShadow.setPosition(this.highStakeIcon.x + 3, this.highStakeIcon.y + 3);
        }
    }

    private setBiggestPossibleFont(textObj: Phaser.GameObjects.Text, width, height) {
        var tempText = textObj.text;

        textObj.text = textObj.text.split('\n').sort(
            function (a, b) {
                return b.length - a.length;
            }
        )[0];

        setTextFontScaledToBox(textObj, width, height);
        textObj.text = tempText;
    }
}